import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router';
import cx from 'classnames';
import map from 'lodash/map';
import { OptionType } from '@design-system/types';
import {
  Combobox,
  Select,
  TextInput,
  ToggleButton
} from '@design-system/components';
import { BasicModal } from '@DesignSystem/modals';
import { Workspace } from '@shared/types';
import { useClientPreferencesContext } from '@shared/hooks/useClientPreferencesContext';
import useOrganizationWorkspaces from '@shared/api/useOrganizationWorkspaces';
import { CreateServiceAccountModalProps } from './CreateServiceAccountModal.type';
import { generateTooltipForCreateButton } from './helpers';
import classes from './CreateServiceAccountModal.module.scss';

const CreateServiceAccountModal: React.FC<CreateServiceAccountModalProps> = ({
  open,
  onClose,
  onPrimaryButtonClick,
  isPrimaryButtonDisabled
}) => {
  const { organizationId } = useParams<{ organizationId: string }>();
  const [serviceName, setServiceName] = useState<string>('');
  const [defaultWorkspaceId, setDefaultWorkspaceId] = useState<string | null>(
    null
  );
  const [
    isAuthorizedWorkspaceToggled,
    setIsAuthorizedWorkspaceToggled
  ] = useState<boolean>(false);
  const [authorizedWorkspaces, setAuthorizedWorkspaces] = useState<
    OptionType[]
  >([]);

  const { data: workspaces } = useOrganizationWorkspaces(organizationId);
  const {
    validateSaWorkspaceEnabled,
    serviceAccountWorkspacesLimit
  } = useClientPreferencesContext();
  const isAuthorizedWorkspaceEnabled =
    validateSaWorkspaceEnabled && isAuthorizedWorkspaceToggled;

  const workspaceOptions = useMemo(
    () =>
      workspaces?.map((workspace: Workspace) => ({
        label: workspace.name,
        value: workspace.id
      })) || [],
    [workspaces]
  );

  useEffect(() => {
    if (!open) {
      setServiceName('');
      setDefaultWorkspaceId(null);
      setIsAuthorizedWorkspaceToggled(false);
      setAuthorizedWorkspaces([]);
    }
  }, [open]);

  const handlePrimaryButtonClick = useCallback(() => {
    onPrimaryButtonClick({
      name: serviceName,
      defaultWorkspaceId: isAuthorizedWorkspaceEnabled
        ? null
        : defaultWorkspaceId,
      authorizedWorkspaces:
        (isAuthorizedWorkspaceEnabled && map(authorizedWorkspaces, 'label')) ||
        []
    });
  }, [
    onPrimaryButtonClick,
    serviceName,
    defaultWorkspaceId,
    isAuthorizedWorkspaceEnabled,
    authorizedWorkspaces
  ]);

  const isAuthorizedWorkspaceRequired =
    isAuthorizedWorkspaceEnabled && !authorizedWorkspaces.length;

  const isCreateButtonDisabled =
    !serviceName.length ||
    isPrimaryButtonDisabled ||
    isAuthorizedWorkspaceRequired;

  const primaryButtonTooltip = generateTooltipForCreateButton(
    serviceName,
    isAuthorizedWorkspaceRequired
  );

  return (
    <BasicModal
      open={open}
      onClose={onClose}
      title="Create Service Account"
      primaryButtonText="Create"
      secondaryButtonText="Cancel"
      onPrimaryButtonClick={handlePrimaryButtonClick}
      onSecondaryButtonClick={onClose}
      isPrimaryButtonDisabled={isCreateButtonDisabled}
      primaryButtonTooltip={primaryButtonTooltip}
      additionalClasses={{ paper: classes.modal }}
      footerClassName={classes.footer}
      contentClassName={classes.contentWrapper}
      disableEnforceFocus
      content={
        <div className={classes.content}>
          <div className={classes.description}>
            Once the service account is created, its API key will be available
            in the service account table.
          </div>

          <div>
            <label htmlFor="service-account-name" className={classes.label}>
              Service account name<span className={classes.required}>*</span>
            </label>
            <TextInput
              id="service-account-name"
              placeholder="Enter name"
              value={serviceName}
              onChange={e => setServiceName(e.target.value)}
            />
          </div>

          <div className={classes.defaultWorkspaceSelect}>
            <p className={cx(classes.label, classes.defaultWorkspaceLabel)}>
              Default workspace
            </p>
            <Select
              options={workspaceOptions}
              value={
                isAuthorizedWorkspaceToggled ? undefined : defaultWorkspaceId
              }
              onValueChange={value => setDefaultWorkspaceId(value as string)}
              isSearchable
              isRegexSearchEnabled
              searchPlaceholder="Search (regex)"
              isDisabled={isAuthorizedWorkspaceToggled}
            />
          </div>

          {validateSaWorkspaceEnabled && (
            <>
              <div className={classes.authWorkspaceToggle}>
                <ToggleButton
                  id="toggle-authorized-workspaces"
                  title="Restrict to specific workspaces"
                  checked={isAuthorizedWorkspaceToggled}
                  onChange={() => setIsAuthorizedWorkspaceToggled(v => !v)}
                />
                <label
                  htmlFor="toggle-authorized-workspaces"
                  className={cx(
                    classes.label,
                    classes.authWorkspaceToggleLabel
                  )}
                >
                  Restrict to specific workspaces
                </label>
              </div>

              {isAuthorizedWorkspaceToggled && (
                <Combobox
                  options={workspaceOptions}
                  value={map(authorizedWorkspaces, 'value')}
                  onValueChange={(_, valuesList) => {
                    setAuthorizedWorkspaces(valuesList as OptionType[]);
                  }}
                  maxSelectedLimit={serviceAccountWorkspacesLimit}
                  disableSelectAll
                  isSearchable
                  isRegexSearchEnabled
                  searchPlaceholder="Search (regex)"
                  aria-label="Authorized workspaces select"
                  className={classes.authWorkspaceSelect}
                />
              )}
            </>
          )}
        </div>
      }
    />
  );
};

export default CreateServiceAccountModal;
